import React, { useEffect, useState } from 'react';
import DataTableColumn from '@salesforce/design-system-react/module/components/data-table/column';
import DataTable from '@salesforce/design-system-react/module/components/data-table';
import DataTableCell from '@salesforce/design-system-react/module/components/data-table/cell';
import { useDispatch, useSelector } from 'react-redux';
import { defiAssetSelector, getAssets } from '../../../store/defi/assetSlice';
import { getNumbersWithDecimalPlaces, getNumberWithCommas } from '../../../utilities/money';
import AssetBreakdown from '../components/AssetBreakdown';
import ChainSplit from '../components/ChainSplit';
import { defiChainSelector, getWalletDefiChain } from '../../../store/defi/chainSlice';
import { getChainInfo } from '../../../utilities/chain';
import { capitalizeFirstLetter } from '../../../utilities/string';

const CustomNameCell = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell className="slds-m-top_xx-large" title={children}>
      {item.logo_url && (
        <img style={{ marginRight: '1rem', height: '20px' }} src={item.logo_url} alt="token icon" />
      )}
      <span style={{ fontWeight: 'bold' }}>{children}</span>
    </DataTableCell>
  );
};
CustomNameCell.displayName = DataTableCell.displayName;

const CustomChainCell = ({ children }) => {
  const chain = getChainInfo(children);

  if (chain) {
    return (
      <DataTableCell title={children}>
        <div className="slds-m-right_xx-small" style={{ display: 'flex', gap: '5px' }}>
          {chain?.logo_url && <img src={chain.logo_url} alt="" height="20" width="20"></img>}
          {chain?.label ? chain.label : (children || '').toUpperCase()}
        </div>
      </DataTableCell>
    );
  } else {
    return (
      <DataTableCell className="slds-m-top_xx-large" title={children}>
        <span style={{ fontWeight: 'bold', color: '#080707' }}>
          {(children || '').toUpperCase()}
        </span>
      </DataTableCell>
    );
  }
};
CustomChainCell.displayName = DataTableCell.displayName;

/**
 * Custom Balance cell to display formatted date
 */
const CustomBalanceCell = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell {...props}>
      {item && item?.total_usd_price ? (
        <>{`$${getNumberWithCommas(getNumbersWithDecimalPlaces(item.total_usd_price))}`}</>
      ) : (
        '--'
      )}
    </DataTableCell>
  );
};

CustomBalanceCell.displayName = DataTableCell.displayName;

const CustomTokenBalanceCell = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell {...props}>
      {item && item?.amount ? (
        <>
          {`${getNumberWithCommas(getNumbersWithDecimalPlaces(item.amount))}`} {item.name}
        </>
      ) : (
        '--'
      )}
    </DataTableCell>
  );
};

CustomTokenBalanceCell.displayName = DataTableCell.displayName;

const CustomPriceCell = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell {...props}>
      {item && item?.price ? (
        <>{`$${getNumberWithCommas(getNumbersWithDecimalPlaces(item.price))}`}</>
      ) : (
        '--'
      )}
    </DataTableCell>
  );
};

CustomPriceCell.displayName = DataTableCell.displayName;

const CustomPriceChange24hCell = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell {...props}>
      {item?.price_change_24h ? (
        parseFloat(item.price_change_24h) < 0 ? (
          <span style={{ color: '#E16032', fontWeight: '600' }}>
            {item.price_change_24h_percentage}% (-$
            {Intl.NumberFormat('en-US').format(Math.abs(parseFloat(item.price_change_24h)))})
          </span>
        ) : (
          <span style={{ color: '#54A77B', fontWeight: '600' }}>
            {item.price_change_24h_percentage}% ($
            {Intl.NumberFormat('en-US').format(Math.abs(parseFloat(item.price_change_24h)))})
          </span>
        )
      ) : (
        '--'
      )}
    </DataTableCell>
  );
};

CustomPriceChange24hCell.displayName = DataTableCell.displayName;

const CustomBalanceChange24hCell = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell {...props}>
      {item?.balance_change_24h ? (
        parseFloat(children) < 0 ? (
          <span style={{ color: '#E16032', fontWeight: '600' }}>
            {getNumbersWithDecimalPlaces(item.balance_change_percentage, 4)}% (-$
            {Intl.NumberFormat('en-US').format(Math.abs(parseFloat(item.balance_change_24h)))})
          </span>
        ) : (
          <span style={{ color: '#54A77B', fontWeight: '600' }}>
            {getNumbersWithDecimalPlaces(item.balance_change_percentage, 4)}% ($
            {Intl.NumberFormat('en-US').format(Math.abs(parseFloat(item.balance_change_24h)))})
          </span>
        )
      ) : (
        '--'
      )}
    </DataTableCell>
  );
};

CustomBalanceChange24hCell.displayName = DataTableCell.displayName;

const Assets = ({ account }) => {
  const dispatch = useDispatch();
  const { assets, loading } = useSelector(defiAssetSelector);
  const { chain_total_amounts, token_chain_breakdown, available_chains } =
    useSelector(defiChainSelector);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [chainTotalAmounts, setChainTotalAmounts] = useState([]);
  const [tokenChainBreakdown, setTokenChainBreakdown] = useState([]);
  const [sortColumn, setSortColumn] = useState('');

  useEffect(() => {
    dispatch(getAssets({ wallet: account }));
    // dispatch(getChainInfo({ wallet: account }));
    dispatch(getWalletDefiChain({ account }));
  }, [account, dispatch]);

  useEffect(() => {
    let newItems = [];
    if (assets?.tokens && assets.tokens.length > 0) {
      newItems.push(...assets.tokens);
    }
    // if (assets?.protocols && assets.protocols.length > 0){
    // newItems.push(...assets.protocols);
    // }
    setItems(newItems);
  }, [assets]);

  useEffect(() => {
    setChainTotalAmounts(chain_total_amounts);
  }, [chain_total_amounts]);

  useEffect(() => {
    setTokenChainBreakdown(token_chain_breakdown);
  }, [token_chain_breakdown]);

  const handleLoadMore = () => {
    if (!loading) {
      setTimeout(() => {
        setHasMore(false);
      }, 1000);
    }
  };

  const handleSort = (props) => {
    const { property, sortDirection } = props;
    setSortColumn(property);
    const currentItems = JSON.parse(JSON.stringify(items));
    if (sortDirection === 'asc') {
      currentItems.sort((first, second) => {
        if (first[property] < second[property]) {
          return -1;
        } else {
          return 1;
        }
      });
    } else {
      currentItems.sort((first, second) => {
        if (first[property] < second[property]) {
          return 1;
        } else {
          return -1;
        }
      });
    }
    setItems(currentItems);
  };

  return (
    <div style={{ display: 'flex', gap: '1rem' }}>
      <div
        style={{
          position: 'relative',
          alignSelf: 'flex-start',
          marginTop: '10px',
          width: '65%',
          maxHeight: `${(assets?.tokens || []).length === 0 ? '100%' : 'calc(100vh - 155px)'}`,
          overflowX: 'scroll',
        }}
      >
        <div style={{ minWidth: '1150px' }}>
          <DataTable
            assistiveText={{
              actionsHeader: 'actions',
              columnSort: 'sort this column',
              columnSortedAscending: 'asc',
              columnSortedDescending: 'desc',
              selectAllRows: 'Select all rows',
              selectRow: 'Select this row',
            }}
            fixedHeader
            fixedLayout
            items={items}
            id="Assets-Table"
            onLoadMore={handleLoadMore}
            onSort={handleSort}
            hasMore={hasMore}
            noRowHover
          >
            <DataTableColumn
              sortable
              isDefaultSortDescending
              label="Name"
              primaryColumn
              property="name"
              isSorted={sortColumn === 'name'}
              width="6rem"
            >
              <CustomNameCell />
            </DataTableColumn>
            <DataTableColumn
              sortable
              isDefaultSortDescending
              label="Chain"
              primaryColumn
              property="chain"
              isSorted={sortColumn === 'chain'}
              width="4rem"
            >
              <CustomChainCell />
            </DataTableColumn>
            <DataTableColumn
              sortable
              isDefaultSortDescending
              label="Balance"
              property="total_usd_price"
              isSorted={sortColumn === 'total_usd_price'}
              width="5rem"
            >
              <CustomBalanceCell />
            </DataTableColumn>
            <DataTableColumn
              sortable
              isDefaultSortDescending
              label="Token Balance"
              property="amount"
              isSorted={sortColumn === 'amount'}
              width="5rem"
            >
              <CustomTokenBalanceCell />
            </DataTableColumn>
            <DataTableColumn
              sortable
              isDefaultSortDescending
              label="Price"
              property="price"
              isSorted={sortColumn === 'price'}
              width="5rem"
            >
              <CustomPriceCell />
            </DataTableColumn>
            <DataTableColumn
              sortable
              isDefaultSortDescending
              label="Price 24 Hour Change"
              property="price_change_24h"
              isSorted={sortColumn === 'price_change_24h'}
              width="5rem"
            >
              <CustomPriceChange24hCell />
            </DataTableColumn>
            <DataTableColumn
              sortable
              isDefaultSortDescending
              label="Balance 24 Hour Change"
              property="balance_change_24h"
              isSorted={sortColumn === 'balance_change_24h'}
              width="5rem"
            >
              <CustomBalanceChange24hCell />
            </DataTableColumn>
          </DataTable>
        </div>
        {(assets?.tokens || []).length === 0 && !hasMore && (
          <div 
            style={{
              margin: 'auto',
              display: 'flex',
              justifyContent: 'center',
              paddingBottom: '50px',
              marginTop: '90px'
            }}
          >
            <div>
              <img alt="" src="/assets/empty-state/empty-folder.png" style={{ height: '150px' }} />
              <p style={{ textAlign: 'center', fontWeight: '700', fontSize: '20px' }}>
                Data not available
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="" style={{ flexGrow: 1, overflow: 'hidden' }}>
        <div>
          <ChainSplit title="Asset Split %" chain_total_amounts={chainTotalAmounts} />
        </div>
        <div>
          <AssetBreakdown
            title="Assets by Amount"
            lightningIcon={false}
            chain_asset_breakdown={tokenChainBreakdown}
            available_chains={available_chains}
            direction="vertical"
          />
        </div>
      </div>
    </div>
  );
};

export default Assets;
